/**
 * LocationsFulfilmentPanel — the 'locations' area panel.
 *
 * Per-location sections bind to the header LocationFilter selection
 * (`useLocation()`), not an in-panel picker — see EditingLocationChip. With
 * no location selected, the per-location sections are skipped entirely; the
 * org-wide StarShipIt / Portless credentials still render. Keying the
 * per-location block on `selectedLocation.id` forces a remount (fresh local
 * state) whenever the header selection changes.
 */
import { useTranslation } from 'react-i18next';
import { MapPin, Truck, Printer, Film, Scale } from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';
import { Separator } from '@/components/ui/separator';
import { SettingsSection } from '../components/console/SettingsSection';
import { LocationSettingsSection } from '../components/LocationSettingsSection';
import { ShippingProviderSettingsSection } from '../components/ShippingProviderSettingsSection';
import { ProductionMethodRoutingSection } from '../components/ProductionMethodRoutingSection';
import { InvoiceSettingsSection } from '../components/InvoiceSettingsSection';
import { LabelPrinterSection } from '../components/LabelPrinterSection';
import { FieryHotFolderSection } from '../components/FieryHotFolderSection';
import { StarShipItSection } from '../sections/StarShipItSection';
import { YunExpressSection } from '../sections/YunExpressSection';
import { YunExpressLocationSettings } from '../components/YunExpressLocationSettings';
import { PortlessSection } from '../sections/PortlessSection';
import { DefaultWeightsSection } from '../components/DefaultWeightsSection';

export function LocationsFulfilmentPanel() {
  const { t } = useTranslation('settings');
  const { selectedLocation } = useLocation();

  return (
    <>
      {selectedLocation ? (
        <div key={selectedLocation.id} className="space-y-4">
          <SettingsSection
            id="location"
            title="Location"
            description="Production enablement and print-file source for this location."
            icon={MapPin}
            scope="location"
          >
            <LocationSettingsSection embedded locationId={selectedLocation.id} />
          </SettingsSection>

          <SettingsSection
            id="shipping-provider"
            title="Shipping & invoicing"
            description="Shipping provider, default print technology, and packing invoices."
            icon={Truck}
            scope="location"
          >
            <div className="space-y-4">
              <ShippingProviderSettingsSection
                embedded
                locationId={selectedLocation.id}
                yunexpressProductFieldBelow
              />
              <YunExpressLocationSettings locationId={selectedLocation.id} />
              <Separator />
              <ProductionMethodRoutingSection embedded locationId={selectedLocation.id} />
              <Separator />
              <InvoiceSettingsSection embedded locationId={selectedLocation.id} />
            </div>
          </SettingsSection>

          <SettingsSection
            id="label-printer"
            title="Label printer"
            description="OS printer name the print agent uses for shipping labels."
            icon={Printer}
            scope="location"
          >
            <LabelPrinterSection locationId={selectedLocation.id} />
          </SettingsSection>

          <SettingsSection
            id="fiery-hot-folder"
            title="DTF Drop Folder"
            description="Watched folder each print PC's RIP consumes, picked on the print PC via the Print Agent."
            icon={Film}
            scope="location"
          >
            <FieryHotFolderSection locationId={selectedLocation.id} />
          </SettingsSection>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          Select a location in the header to edit its per-location settings. StarShipIt, Portless, YunExpress
          and default shipping weights below are organization-wide.
        </div>
      )}

      <StarShipItSection />
      <PortlessSection />
      <YunExpressSection />

      {/* Title + description come from the `settings` namespace (Ningbo
          operators read zh) and are kept in lockstep with the same card's
          `areas.ts` entry, which reads the same keys. */}
      <SettingsSection
        id="default-weights"
        title={t('production.defaultWeights.title')}
        description={t('production.defaultWeights.description')}
        icon={Scale}
        scope="org"
      >
        <DefaultWeightsSection />
      </SettingsSection>
    </>
  );
}
